import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import SubmitButton from '@/components/common/Button';
import { Button } from '@/components/shadcn/button';
import cn from '@/lib/utils/cn';
import type { Action, ActionType } from '@/types/actions';

import useAction from '../../hooks/useAction';

import ActionIcon from './ActionIcon';
import ActionSourceIcon from './ActionSourceIcon';

type Props = Action;

const ActionLink = ({
  to,
  type,
  onClick,
}: {
  to: string;
  type: ActionType;
  onClick?: () => void;
}) => {
  const { t } = useTranslation();

  const blank = to.startsWith('http');

  return (
    <Button size="sm" className="h-6 text-xs" variant="default" asChild>
      <Link to={to} target={blank ? '_blank' : ''} onClick={onClick}>
        {t(`ACTION_TYPE_CTA_${type}`)}

        <ArrowRight className="size-3" strokeWidth={2.5} />
      </Link>
    </Button>
  );
};

const ActionButton = ({
  handleClick,
  type,
}: {
  handleClick: () => Promise<void> | void;
  type: ActionType;
}) => {
  const { t } = useTranslation();

  return (
    <SubmitButton
      size="sm"
      className="h-6 text-xs"
      variant="default"
      onSubmit={handleClick}
    >
      {t(`ACTION_TYPE_CTA_${type}`)}

      <ArrowRight className="size-3" strokeWidth={2.5} />
    </SubmitButton>
  );
};

export default function AnalysisAction(action: Props) {
  const { title, source, description, type } = action;

  const { isClickable, isCompleted, to, handleActionClick, trackClick } =
    useAction(action);

  return (
    <div
      className={cn(
        'bg-primary-50 dark:bg-primary-800/50 relative flex items-start gap-2 rounded-md p-2 transition-opacity',
        {
          ['opacity-35']: isCompleted,
        },
      )}
    >
      <div className="flex w-full flex-col gap-1">
        <div>
          <div className="mr-3.5 flex items-center gap-2">
            <ActionIcon type={type} />

            <p className="text-sm leading-tight font-bold">{title}</p>
          </div>

          <ActionSourceIcon source={source} />
        </div>

        <div className="text-foreground/85 text-[13px]">{description}</div>

        {isClickable && (
          <div className="mt-1 flex w-full flex-col items-end">
            {to ? (
              <ActionLink to={to} type={action.type} onClick={trackClick} />
            ) : (
              <ActionButton
                handleClick={handleActionClick}
                type={action.type}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
